import React from "react";
import {
  Button,
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  List,
  ListActions,
  NumberInput,
  ReferenceField,
  ReferenceInput,
  SelectInput,
  ShowButton,
  SimpleList,
  TextField,
  Title,
  useStore,
} from "react-admin";
import { Box, Paper, Typography, useMediaQuery } from "@mui/material";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { checkPermission } from "server/security/utils";
import { getPermissions } from "auth/security/securityUtils";
import { administrationRequests } from "api/administrationApi";
import { graduationRequests } from "api/graduationApi";
import RoomField from "server/components/Fields/RoomField";
import CustomFilter from "features/CustomFilter";
import useInternshipFilter from "features/filters/useInternshipFilter";
import RoomInput from "server/components/Fields/RoomInput";
import {
  INDIVIDUAL_SLOTS,
  InternshipPlanScheduleTypes,
  GraduationServicePath,
  MOBILE_BREAKPOINT,
  SCHEDULES,
} from "utils/constants";
import TimeIntervalsExpanded from "./TimeIntervalsExpanded";
import TimeIntervalsMobileExpanded from "./TimeIntervalsMobileExpanded";
import { useNavigate } from "react-router";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import { settings } from "app";
import { MobileListRow } from "features/MobileListRow";

const STORE_KEY = "internshipIndividualSchedule.filters";

const ListScheduleIndividualSlot = () => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const internship = useInternshipFilter({ filters, withPlan: true });

  const handleSearch = selectedFilters => {
    setFilters(selectedFilters);
  };

  const slotFilters = [
    <RoomInput
      key="roomId"
      alwaysOn
      style={{ width: 300 }}
    />,
    <ReferenceInput
      key="disciplineId"
      source="disciplineId"
      reference={graduationRequests.getPlanDisciplinesSelect(filters?.planId)}
      filter={{ planScheduleType: InternshipPlanScheduleTypes.Individual }}
      alwaysOn
    >
      <SelectInput
        label="Дисциплина"
        optionText="nameBg"
        emptyText={false}
        style={{ width: 300 }}
      />
    </ReferenceInput>,
    <NumberInput
      key="boxNumber"
      source="boxNumber"
      label="Бокс номер"
      alwaysOn
    />,
  ];

  return (
    <>
      {!filters && <Title title={"Индивидуални разписания"} />}
      <CustomFilter
        title="Избери филтри"
        cardWidth={isMobile ? "100%" : "700px"}
        filters={internship.fields}
        buttonLabel="Покажи"
        buttonDisabled={!internship.isComplete}
        buttonOnClick={() => handleSearch(internship.query)}
      />
      {!filters ? (
        <Paper sx={{ mt: 5 }}>
          <Box
            p={2}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              align="center"
              color="secondary"
            >
              Въведи филтри за индивидуално разписание или създай ново
            </Typography>
            <SecuredComponent permission={permissions.canManageInternshipSchedules}>
              <CreateButton />
            </SecuredComponent>
          </Box>
        </Paper>
      ) : (
        <List
          exporter={false}
          title="Индивидуални разписания"
          filters={slotFilters}
          filter={filters}
          actions={
            <ListActions hasCreate={checkPermission(getPermissions(), permissions.canManageInternshipSchedules)} />
          }
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow fields={[{ label: "Зала", value: <RoomField /> }]} />
                  <MobileListRow
                    fields={[
                      {
                        label: "Дисциплина",
                        value: (
                          <ReferenceField
                            source="disciplineId"
                            reference={administrationRequests.getDisciplinesSelect()}
                          >
                            <TextField source="name" />
                          </ReferenceField>
                        ),
                      },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <>
                  <div>
                    <MobileListRow fields={[{ label: "Бокс номер", value: record?.boxNumber }]} />
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      <SecuredComponent permission={permissions.canPreviewInternshipSchedules}>
                        <Button
                          label="Студенти"
                          variant="contained"
                          startIcon={<PeopleAltIcon />}
                          onClick={() =>
                            navigate(
                              `/${GraduationServicePath}${INDIVIDUAL_SLOTS}/${SCHEDULES}/${record?.id}/intervals/students`,
                              {
                                state: { record },
                              },
                            )
                          }
                        />
                      </SecuredComponent>
                    </div>
                  </div>
                  <TimeIntervalsMobileExpanded record={record} />
                </>
              )}
              tertiaryText={() => (
                <>
                  <SecuredComponent permission={permissions.canPreviewInternshipSchedules}>
                    <ShowButton label="" />
                  </SecuredComponent>
                  <SecuredComponent permission={permissions.canManageInternshipSchedules}>
                    <EditButton label="" />
                  </SecuredComponent>
                </>
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={false}
              rowClick={false}
              expand={<TimeIntervalsExpanded />}
              expandSingle
            >
              <RoomField label="Зала" />
              <ReferenceField
                source="disciplineId"
                reference={administrationRequests.getDisciplinesSelect()}
                label="Дисциплина"
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
              <TextField
                source="boxNumber"
                label="Бокс номер"
                sortable={false}
              />
              <SecuredComponent permission={permissions.canPreviewInternshipSchedules}>
                <ShowButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canManageInternshipSchedules}>
                <EditButton />
              </SecuredComponent>
              <SecuredComponent permission={permissions.canPreviewInternshipSchedules}>
                <FunctionField
                  render={rec => {
                    const slotId = rec?.id;
                    return (
                      <Button
                        label="Студенти"
                        variant="contained"
                        startIcon={<PeopleAltIcon />}
                        onClick={() =>
                          navigate(
                            `/${GraduationServicePath}${INDIVIDUAL_SLOTS}/${SCHEDULES}/${slotId}/intervals/students`,
                            {
                              state: { record: rec },
                            },
                          )
                        }
                      />
                    );
                  }}
                />
              </SecuredComponent>
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListScheduleIndividualSlot;
